import React, { ReactNode, useRef, useState } from 'react';

import styled from 'styled-components';

import { FileFieldProps } from '@components/file-field';
import {
  FileInput,
  InstantUploadFileInput,
  InstantUploadFileInputProps,
  useInstantFile,
} from '@components/file-field/file-input';
import { IconImage } from '@components/icons/image';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

export type LogoFieldProps = Omit<FileFieldProps, 'defaultValue'> & {
  defaultImage?: string;
  label?: string;
  dataTestid?: string;
  fileType?: 'image' | 'svg';
};

type BaseLogoFieldProps = Omit<LogoFieldProps, 'onChange'> & {
  children: ReactNode;
  inputRef?: React.RefObject<HTMLInputElement>;
  url?: string;
  fileType?: 'image' | 'svg';
};

const BaseLogoField = ({
  label: userLabel,
  dataTestid,
  inputRef,
  url,
  children,
  fileType = 'image',
}: BaseLogoFieldProps) => {
  const { t } = useScopedTranslate(locale);
  const label = userLabel ?? t('logo');

  return (
    <>
      {children}
      <Stack orientation="horizontal" space={4}>
        <LogoContainer data-testid={dataTestid} onClick={() => inputRef?.current?.click()}>
          {url ? <Logo alt={label} src={url} /> : <IconImage />}
        </LogoContainer>
        <Stack justify="center">
          <Text font="semibold14" text={label} />
          <Text
            color="textHelper"
            font="regular14"
            text={fileType === 'svg' ? t('acceptedSvg') : t('accepted')}
          />
        </Stack>
      </Stack>
    </>
  );
};

export const LogoField = ({
  onChange,
  defaultImage,
  fileType = 'image',
  ...rest
}: LogoFieldProps) => {
  const [file, setFile] = useState<File | null>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const url = file ? window.URL.createObjectURL(file) : defaultImage ?? '';

  return (
    <BaseLogoField {...rest} url={url} inputRef={fileInputRef} fileType={fileType}>
      <FileInput
        ref={fileInputRef}
        fileType={fileType}
        onChange={(newFile) => {
          setFile(newFile);
          onChange(newFile);
        }}
      />
    </BaseLogoField>
  );
};
export type InstantUploadLogoFieldProps = Omit<LogoFieldProps, 'onChange'> &
  Pick<InstantUploadFileInputProps, 'onSelect' | 'onChange' | 'defaultValue'>;

export const InstantUploadLogoField = ({
  onChange,
  onSelect,
  defaultValue,
  fileType = 'image',
  ...rest
}: InstantUploadLogoFieldProps) => {
  const [file, setFile] = useInstantFile(defaultValue);
  const fileInputRef = useRef<HTMLInputElement>(null);

  return (
    <BaseLogoField
      {...rest}
      url={file?.fullUrl ?? file?.url}
      inputRef={fileInputRef}
      fileType={fileType}
    >
      <InstantUploadFileInput
        ref={fileInputRef}
        fileType={fileType}
        onChange={onChange}
        onSelect={onSelect}
        setFile={setFile}
      />
    </BaseLogoField>
  );
};

const LogoContainer = styled.div({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  borderStyle: 'solid',
  borderRadius: pxToRem(8),
  borderWidth: pxToRem(2),
  borderColor: theme.color.border10,
  width: pxToRem(64),
  height: pxToRem(64),
  backgroundColor: theme.color.uiTrans5,
  color: theme.color.uiTrans50,
  cursor: 'pointer',
});

const Logo = styled.img({
  width: pxToRem(32),
  height: pxToRem(32),
});

const locale = {
  en: {
    logo: 'Logo',
    accepted: 'Accepted file types: .png and .jpg',
    acceptedSvg: 'Accepted file types: .svg',
  },
};
